import { defineStore } from 'pinia'
import { store } from '@/store'
import { cloneDeep } from 'lodash-es'
import remainingRouter from '@/router/modules/remaining'
import { Layout, flatMultiLevelRoutes, generateRoute } from '@/utils/routerHelper'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'
import { DynamicRouterRegistry } from '@/router/modules/dynamic'
import { loadFirmModulesForMenu } from '@/composables/useFirmModules'
import { filterMenusByFirmModules } from '@/utils/firmModules'
import { filterMenusByBizMode, loadBizModeForMenu } from '@/utils/bizModeMenus'
import { appendBusinessMenus } from '@/utils/businessMenuExtras'
import {
  buildAiApplicationCenterRoutes,
  buildAiBusinessCenterRoutes
} from '@/router/aiApplicationCenter'
import microSceneRoutes from '@/router/modules/microscene'
import surveyRoutes from '@/router/modules/survey'
import type { AppCustomRouteRecordRaw, AppRouteRecordRaw } from '@/router/types'
import { useUserStoreWithOut } from '@/store/modules/user'
import { EXTERNAL_TENANT_HIDDEN_STATIC_PATHS } from '@/layout/components/Menu/src/menuWorkbenchConfig'

const { wsCache } = useCache()

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  menuRouters: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  menuTabRouters: AppRouteRecordRaw[]
  menuRootPath: string
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    menuRouters: [],
    addRouters: [],
    menuTabRouters: [],
    menuRootPath: ''
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getMenuRouters(): AppRouteRecordRaw[] {
      return this.menuRouters
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    },
    getMenuRootPath(): string {
      return this.menuRootPath
    }
  },
  actions: {
    async generateRoutes(): Promise<void> {
      // 加载动态模块路由（热插拔模块），先加载以获取已启用模块列表
      await DynamicRouterRegistry.loadEnabledModules()

      // 获得菜单列表，它在登录的时候，setUserInfoAction 方法中已经进行获取
      let res: AppCustomRouteRecordRaw[] = []
      const roleRouters: AppCustomRouteRecordRaw[] | undefined = wsCache.get(CACHE_KEY.ROLE_ROUTERS)
      if (roleRouters) {
        res = roleRouters
      }

      // 根据已启用模块过滤菜单：隐藏未启用模块的菜单项
      const enabledFiltered = filterMenusByEnabledModules(res)
      const firmModules = await loadFirmModulesForMenu().catch(() => ({}))
      const firmFiltered = filterMenusByFirmModules(enabledFiltered, firmModules)
      // SELF(B2C)：入驻律所侧栏改名为「官方本所」；与 firmErpEnabled 解耦
      const bizMode = await loadBizModeForMenu().catch(() => 'PLATFORM')
      const bizFiltered = filterMenusByBizMode(firmFiltered, bizMode)
      // 活动「数据分析」与概况同页，侧栏去掉该分组（库表软删见 sql1007）
      const filteredRouters = filterRedundantActivityAnalyticsMenus(bizFiltered)
      const routerMap: AppRouteRecordRaw[] = generateRoute(filteredRouters)
      // 餐饮统计仍为独立壳展平叶子跳转；跑腿已独立为 AI 应用中心独立窗。
      // 餐饮管理（/ai-Application-center/catering）已统一为内嵌管理树，不再展平。
      flattenCateringTopMenus(routerMap)
      prependModuleOverviewRoutes(routerMap)
      // 后端 system_menu 生成的是裸模块路径（例如 /hotel/room），而独立窗口会将
      // 导航统一改写为 /ai-Application-center/hotel/room。此前这里只给静态/模块路由
      // 生成前缀副本，后端权限路由没有副本，导致酒店独立窗除静态首页外全部命中 404。
      // 前缀路由只进入路由表，不进入菜单树，避免侧栏出现重复菜单。
      const prefixedPermissionRoutes = [
        ...buildAiApplicationCenterRoutes(routerMap),
        ...buildAiBusinessCenterRoutes(routerMap)
      ]
      this.menuRouters = cloneDeep(routerMap)
      // 侧栏菜单树（Menu.vue 用的是 getMenuRouters）也要补这条
      appendBusinessMenus(this.menuRouters)

      // 动态路由，404一定要放到最后面
      // preschooler：vue-router@4以后已支持静态404路由，此处可不再追加
      this.addRouters = routerMap.concat([
        {
          path: '/:path(.*)*',
          // redirect: '/404',
          component: () => import('@/views/Error/404.vue'),
          name: '404Page',
          meta: {
            hidden: true,
            breadcrumb: false
          }
        }
      ])
      // 获取动态模块路由并合并
      const dynamicRoutes = DynamicRouterRegistry.getAllRoutes()
      const staticRouters = filterStaticRoutersForExternalTenant(cloneDeep(remainingRouter))

      // 渲染菜单的所有路由（静态 + 后端菜单 + 动态模块）
      this.routers = staticRouters
        .concat(routerMap)
        .concat(prefixedPermissionRoutes)
        .concat(surveyRoutes)
        .concat(microSceneRoutes)
        .concat(dynamicRoutes)
      // 业务中台侧栏兜底补「APP配置」入口（后端菜单里还没登记这条）
      appendBusinessMenus(this.routers)
      this.addRouters = routerMap
        .concat(prefixedPermissionRoutes)
        .concat(surveyRoutes)
        .concat(microSceneRoutes)
        .concat(dynamicRoutes)
        .concat([
          {
            path: '/:path(.*)*',
            component: () => import('@/views/Error/404.vue'),
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    },
    setMenuRootPath(path: string): void {
      this.menuRootPath = path
    }
  },
  persist: false
})

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}

const MODULE_OVERVIEW_CONFIGS = [
  // 工作台「会员管理」不插前端概况：后端菜单树已够用，概况页为通用空壳
  // {
  //   rootPath: '/member',
  //   title: '会员管理',
  //   icon: 'ep:user-filled',
  //   moduleKey: 'member',
  //   name: 'MemberDashboard'
  // },
  {
    rootPath: '/ai-Application-center/activity',
    title: '活动报名',
    icon: 'ep:calendar',
    moduleKey: 'activity',
    name: 'ActivityDashboard'
  },
  {
    rootPath: '/ai-Application-center/cms',
    title: '内容管理',
    icon: 'ep:document',
    moduleKey: 'cms',
    name: 'CmsDashboard'
  },
  // AI 业务中台 — 根路由无 redirect 时需概览页兜底，否则匹配到 Layout 但无子路由渲染
  {
    rootPath: '/ai-business-center/crm',
    title: 'CRM 系统',
    icon: 'ep:user-filled',
    moduleKey: 'crm',
    name: 'CrmDashboard'
  },
  {
    rootPath: '/ai-business-center/erp',
    title: 'ERP 系统',
    icon: 'ep:goods',
    moduleKey: 'erp',
    name: 'ErpDashboard'
  },
  {
    rootPath: '/ai-business-center/wms',
    title: 'WMS 系统',
    icon: 'ep:box',
    moduleKey: 'wms',
    name: 'WmsDashboard'
  },
  {
    rootPath: '/ai-business-center/mes',
    title: 'MES 系统',
    icon: 'ep:cpu',
    moduleKey: 'mes',
    name: 'MesDashboard'
  },
  {
    rootPath: '/ai-business-center/report',
    title: '报表管理',
    icon: 'ep:data-analysis',
    moduleKey: 'report',
    name: 'ReportDashboard'
  },
  {
    rootPath: '/ai-Application-center/route',
    title: '线路管理',
    icon: 'ep:map-location',
    moduleKey: 'route',
    name: 'RouteDashboard'
  }
] as const

const getRouteRootPath = (path = '') => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const parts = normalizedPath.split('/').filter(Boolean)
  // 中心前缀路径（/ai-business-center/xxx 或 /ai-Application-center/xxx）取 2 段，普通路径取 1 段
  const first = parts[0]
  const rootLen = first === 'ai-Application-center' || first === 'ai-business-center' ? 2 : 1
  return '/' + parts.slice(0, rootLen).join('/')
}

function createOverviewRoute(config: (typeof MODULE_OVERVIEW_CONFIGS)[number]): AppRouteRecordRaw {
  return {
    path: 'dashboard',
    component: () => import('@/views/moduleOverview/index.vue'),
    name: config.name,
    meta: {
      title: '概况',
      icon: 'ep:data-analysis',
      canTo: true,
      moduleKey: config.moduleKey
    }
  }
}

function prependModuleOverviewRoutes(routes: AppRouteRecordRaw[]) {
  MODULE_OVERVIEW_CONFIGS.forEach((config) => {
    let rootRoute = routes.find((route) => {
      const title = String(route.meta?.title || '')
      return getRouteRootPath(route.path) === config.rootPath || title === config.title
    })
    if (!rootRoute) {
      // 会员中心：禁止无后端菜单时造「仅概况」空壳；有权限时由 get-permission-info 带回整树
      if (config.rootPath === '/member') {
        return
      }
      rootRoute = {
        path: config.rootPath,
        component: Layout,
        name: `${config.name}Root`,
        redirect: `${config.rootPath}/dashboard`,
        meta: {
          title: config.title,
          icon: config.icon,
          alwaysShow: true
        },
        children: []
      }
      routes.push(rootRoute)
    }

    const children = rootRoute.children || []
    rootRoute.children = children.filter((child) => child.path !== 'dashboard')
    rootRoute.children.unshift(createOverviewRoute(config))
    // redirect 使用实际路由路径而非 config.rootPath，确保中心前缀正确（如 /ai-business-center/crm/dashboard）
    if (!rootRoute.redirect) {
      rootRoute.redirect = `${rootRoute.path}/dashboard`
    }
    rootRoute.meta = {
      ...(rootRoute.meta || {}),
      alwaysShow: true
    }
  })
}

/**
 * 餐饮统计顶级菜单展平为叶子跳转路由。
 *
 * 背景：餐饮统计顶级对应主框架侧边栏中的独立入口，点击后应直接进入独立餐饮壳，
 * 而不是渲染在主框架 Layout 内展开嵌套子菜单。
 * 跑腿已独立为 AI 应用中心独立窗（app=errand），不再并入餐饮跳转。
 */
const CATERING_TOP_PATHS = new Set([
  '/ai-Application-center/catering-statistics'
])

function flattenCateringTopMenus(routes: AppRouteRecordRaw[]) {
  for (let i = 0; i < routes.length; i++) {
    const route = routes[i]
    if (!route || !CATERING_TOP_PATHS.has(route.path || '')) continue
    routes[i] = {
      path: route.path as string,
      name: `${String(route.name || 'CateringTop')}CateringJump`,
      component: () => import('@/views/catering/redirect/page.vue'),
      meta: {
        ...(route.meta || {}),
        hidden: false,
        alwaysShow: false
      },
      children: undefined,
      redirect: undefined
    } as AppRouteRecordRaw
  }
}

/**
 * 活动报名「数据分析」分组（运营看板 / 活动复盘）与概况同页，侧栏隐藏。
 * 对应菜单 ID：9004307 / 9004308 / 9004309
 */
function filterRedundantActivityAnalyticsMenus(
  routers: AppCustomRouteRecordRaw[]
): AppCustomRouteRecordRaw[] {
  if (!routers?.length) return routers

  const REDUNDANT_IDS = new Set([9004307, 9004308, 9004309])

  return routers
    .map((route): AppCustomRouteRecordRaw | null => {
      const filteredChildren = route.children?.length
        ? filterRedundantActivityAnalyticsMenus(route.children)
        : route.children

      const id = Number((route as AppCustomRouteRecordRaw & { id?: number }).id)
      const path = String(route.path || '')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')
      const moduleCode = String(route.moduleCode || '')
      const title = String(route.name || '')
      const childPaths = (route.children || []).map((c) =>
        String(c.path || '')
          .replace(/^\/+/, '')
          .replace(/\/+$/, '')
      )

      const isActivityAnalyticsDir =
        id === 9004307 ||
        ((moduleCode === 'activity' || moduleCode === '') &&
          (path === 'analytics' || path.endsWith('/analytics')) &&
          (title === '数据分析' ||
            childPaths.includes('dashboard') ||
            childPaths.includes('review') ||
            (route.children || []).some((c) =>
              REDUNDANT_IDS.has(Number((c as AppCustomRouteRecordRaw & { id?: number }).id))
            )))

      if (isActivityAnalyticsDir) return null

      if (
        REDUNDANT_IDS.has(id) ||
        (moduleCode === 'activity' &&
          (path === 'dashboard' || path === 'review') &&
          String(route.component || '').includes('moduleOverview'))
      ) {
        return null
      }

      if (
        route.children &&
        route.children.length > 0 &&
        (!filteredChildren || filteredChildren.length === 0) &&
        !route.component
      ) {
        return null
      }

      return { ...route, children: filteredChildren }
    })
    .filter((route): route is AppCustomRouteRecordRaw => route !== null)
}

/**
 * 根据已启用模块列表过滤菜单
 * 隐藏属于未启用模块的菜单项
 */
function filterMenusByEnabledModules(
  routers: AppCustomRouteRecordRaw[]
): AppCustomRouteRecordRaw[] {
  if (!routers || routers.length === 0) return routers

  return routers
    .map((route): AppCustomRouteRecordRaw | null => {
      // 递归过滤子菜单
      let filteredChildren: AppCustomRouteRecordRaw[] | undefined
      if (route.children && route.children.length > 0) {
        filteredChildren = filterMenusByEnabledModules(route.children)
      }

      // MenuVO only supplies a stable module signal through its path.
      const moduleName = extractModuleName(route)
      if (moduleName) {
        // 有明确模块归属 → 检查是否启用
        if (!DynamicRouterRegistry.isModuleEnabled(moduleName)) {
          return null // 未启用 → 隐藏
        }
        return { ...route, children: filteredChildren }
      }

      // 无模块归属（如目录、外链）
      // 但如果有子菜单且全部被过滤掉了 → 也隐藏自己
      if (
        route.children &&
        route.children.length > 0 &&
        (!filteredChildren || filteredChildren.length === 0)
      ) {
        return null
      }

      return { ...route, children: filteredChildren }
    })
    .filter((route): route is AppCustomRouteRecordRaw => route !== null)
}

/**
 * 从菜单项提取模块名
 * 支持两层路径：/module/xxx 或 /ai-Application-center/module/xxx。
 */
function extractModuleName(route: AppCustomRouteRecordRaw): string | null {
  const path = route.path || ''

  // 底座路径优先：目录 permission 仍是 ai:api-key:* 时，不能误判成产品模块 ai
  if (path === '/ai-platform' || path.startsWith('/ai-platform/')) {
    return 'aiPlatform'
  }
  if (path === '/ai/platform' || path.startsWith('/ai/platform/')) {
    return 'aiPlatform'
  }
  if (path === '/ai-secretary' || path.startsWith('/ai-secretary/')) {
    return 'aiSecretary'
  }

  // 后端原始菜单的 permission 位于路由对象本身；generateRoute 之后才会转换为 meta。
  const perm = (route.permission as string) || (route.meta?.permission as string) || ''
  if (perm && perm.includes(':')) {
    // 微名片：权限仍挂 bosscircle 前缀，热拔插模块码为 digitalCard
    if (perm.startsWith('bosscircle:digital-card:')) {
      return 'digitalCard'
    }
    // 平台分销：权限前缀 platform:dist-*，非产品热拔插模块
    if (perm.startsWith('platform:dist')) {
      return 'system'
    }
    // 认证审核：微名片菜单也复用 bosscircle:certification:*；只开 digitalCard 时按 path 鉴权
    if (perm.startsWith('bosscircle:certification:')) {
      const p = (route.path as string) || ''
      if (p === '/digital-card' || p.startsWith('/digital-card/')) {
        return 'digitalCard'
      }
    }
    // 创作者审核中心挂在 AI商务社交，权限仍用 short-video:creator:*；按组件归属 business
    if (perm.startsWith('short-video:creator:')) {
      const component = String(route.component || '')
      if (component.includes('business/creator-audit')) {
        return 'business'
      }
    }
    const module = perm.split(':')[0]
    // Boss圈的权限命名空间与产品模块注册名不同；微名片例外已在上面处理。
    if (module === 'bosscircle') return 'business'
    if (module === 'short-video') return 'shortvideo'
    return module
  }
  // 策略2：path 前缀（/member/user → member，或 /ai-Application-center/cms → cms）

  if (path.startsWith('/')) {
    const parts = path.split('/')
    // 跳过中心前缀段：/ai-Application-center/module → parts[2], /ai-business-center/module → parts[2]
    let segIdx = 1
    if (parts[1] === 'ai-Application-center' || parts[1] === 'ai-business-center') {
      segIdx = 2
    }
    if (parts.length > segIdx && parts[segIdx]) {
      const module = parts[segIdx]
      // /ai-platform/* → aiPlatform；/ai/platform/* 旧链兼容期仍识别
      if (module === 'ai-platform') {
        return 'aiPlatform'
      }
      if (module === 'ai' && parts.length > segIdx + 1 && parts[segIdx + 1] === 'platform') {
        return 'aiPlatform'
      }
      if (module === 'ai-secretary') {
        return 'aiSecretary'
      }
      // 餐饮独立窗 URL 前缀 /repast（兼容旧 /catering-app）；/catering 是 FD 新后台，模块仍为 food。
      if (module === 'repast' || module === 'catering' || module === 'catering-app') {
        return 'food'
      }
      // 餐饮统计独立顶级菜单（从餐饮管理中单独提出），模块仍为 food
      if (module === 'catering-statistics') {
        return 'food'
      }
      // 紫金餐厅收银：正式前缀 /canteen（兼容旧 /ai-shouyin、/daike-app）；鉴权模块仍为 daike
      if (
        module === 'canteen' ||
        module === 'ai-shouyin' ||
        module === 'daike-app' ||
        module === 'daike'
      ) {
        return 'daike'
      }
      // 景区独立窗前缀 /scenicarea；业务菜单仍 /scenic，模块注册表均为 scenic
      if (module === 'scenicarea') {
        return 'scenic'
      }
      // 微场景独立窗前缀 /scene；业务菜单仍 /micro-scene，模块注册表为 microscene
      if (module === 'scene' || module === 'micro-scene') {
        return 'microscene'
      }
      // 问卷独立窗前缀 /questionnaire；业务菜单仍 /survey，模块注册表为 survey
      if (module === 'questionnaire') {
        return 'survey'
      }
      if (module === 'short-video') return 'shortvideo'
      // A 端上门服务 URL 前缀 /visiting，模块注册表仍为 o2o
      if (module === 'visiting') {
        return 'o2o'
      }
      // A 端配送系统 URL 前缀 /distribution，模块注册表仍为 capacity
      if (module === 'distribution') {
        return 'capacity'
      }
      // 微名片：菜单 path=/digital-card，system_module.name=digitalCard
      if (module === 'digital-card') {
        return 'digitalCard'
      }
      // 评价治理：一级目录 /content-security 与顶级页面菜单 /review-dashboard 的首段
      // 都不等于 system_module.name=review/contentsec，MenuVO 又不带 permission，只能按 path 认模块。
      if (module === 'content-security') {
        return 'contentsec'
      }
      if (module === 'review-dashboard') {
        return 'review'
      }

      // 赛事管理子菜单路径映射到 event 模块
      const eventSubPaths = [
        'template',
        'templateCategory',
        'templateTag',
        'preparation',
        'registration',
        'scheduling',
        'execution',
        'result',
        'fee',
        'notification',
        'resource',
        'sponsor',
        'analytics',
        'category',
        'venue',
        'scoring',
        'participant',
        'schedule',
        'stage',
        'referee',
        'appeal',
        'certificate'
      ]
      if (eventSubPaths.includes(module)) {
        return 'event'
      }
      // 历史 path=/capacity-org（若仍存在）归入 capacity，避免被热拔插过滤
      if (module === 'capacity-org' || module === 'delivery') {
        return 'capacity'
      }
      // A 端商家主体 SSOT：/merchant-ops 归入 merchant 模块
      if (module === 'merchant-ops') {
        return 'merchant'
      }
      // 门户解决方案：归入常驻 system
      if (module === 'portal-solution' || module === 'portal-solution-admin') {
        return 'system'
      }
      // 渠道代理商中台：平台常驻，不走产品热拔插
      if (module === 'channel-agent' || module === 'agent') {
        return 'system'
      }
      // 平台分销：平台常驻，不走产品热拔插
      if (module === 'platform-dist') {
        return 'system'
      }
      return module
    }
  }
  return null
}

/** 外部租户隐藏 remaining 中的门户静态壳（微信小店 / 工作台大屏；域名与站点留给自配） */
function filterStaticRoutersForExternalTenant(routes: AppRouteRecordRaw[]): AppRouteRecordRaw[] {
  const userStore = useUserStoreWithOut()
  if (!userStore.isExternalTenant) {
    return routes
  }
  return routes.filter((route) => {
    const path = route.path || ''
    return !EXTERNAL_TENANT_HIDDEN_STATIC_PATHS.some(
      (hidden) => path === hidden || path.startsWith(`${hidden}/`)
    )
  })
}
